import React, { HTMLProps } from 'react'
import {
    useReactTable,
    getCoreRowModel,
    flexRender,
    getSortedRowModel,
    PaginationState
} from '@tanstack/react-table'
import type { Table, Row, TableOptions, HeaderGroup, SortingState, RowSelectionState, Updater } from '@tanstack/react-table'
import { ModelMeta, SetMetaInput } from 'src/state/Module'
import { Box, Button, Group, Select } from '@mantine/core'
import * as S from './DataTableStyles'
import { Icon } from '@iconify/react'
import { useAppState } from 'src/state'
import { clsx } from 'clsx'

export interface TableProperties<T extends Record<string, unknown>> extends Pick<TableOptions<T>, 'columns' | 'data' | 'initialState'> {
    loading: boolean;
    meta: ModelMeta<T>;
    onMetaChange: (meta: SetMetaInput<T>) => void;
    onAdd?: (instance: Table<T>) => React.MouseEventHandler;
    onDelete?: (instance: Table<T>) => React.MouseEventHandler;
    onEdit?: (instance: Table<T>) => React.MouseEventHandler;
    onClick?: (row: Row<T>) => void;
    onSelectItem?: (id: any) => any;
    columActions?: ColumnActions
    selectedId?: string | null | undefined;
    highlights?: {
        [index: string]: boolean
    },
    rowSelection?: RowSelectionState
    setRowSelection?: (selected: Updater<RowSelectionState>) => void
}

type ColumnAction = {
    label: string,
    action?: (id: string | number) => void,
    actions?: ColumnActions
}

export type ColumnActions = {
    [index: string]: ColumnAction
}

export type TableComponentProps<T extends Record<string, unknown>> = {
    onEditAction?(id: T['id']): any
    currentFilter?: any;
}

export interface CustomHeaderGroup<T> extends HeaderGroup<T> {
    relationId: string;
    sortId?: string;
    isPlaceholder?: boolean;
}

export function IndeterminateCheckbox({
    indeterminate,
    className = '',
    ...rest
  }: { indeterminate?: boolean } & HTMLProps<HTMLInputElement>) {
    const ref = React.useRef<HTMLInputElement>(null!)
  
    React.useEffect(() => {
      if (typeof indeterminate === 'boolean') {
        ref.current.indeterminate = !rest.checked && indeterminate
      }
    }, [ref, indeterminate])
  
    return (
      <input
        type="checkbox"
        ref={ref}
        className={className + ' cursor-pointer'}
        {...rest}
      />
    )
  }

export function DataTable<T extends Record<string, unknown>>(props: React.PropsWithChildren<TableProperties<T>>): React.ReactElement {

    useAppState();

    const { columns, onSelectItem = () => {}, selectedId, meta: { page: currentPage, total, limit, sortKey, sortOrder } } = props;
    const { onMetaChange, setRowSelection } = props;

    const pageCount = Math.ceil(total / limit);
    const canPreviousPage = currentPage > 1;
    const canNextPage = currentPage < pageCount;

    const pagination = React.useMemo<PaginationState>(
        () => ({
            pageIndex: currentPage - 1,
            pageSize: limit,
        }),
        [currentPage, limit]
    )

    const sorting = React.useMemo<SortingState>(
        () => ([
            {
                id: sortKey as string,
                desc: sortOrder === 'desc'
            }
        ]),
        [sortKey, sortOrder]
    )

    const table = useReactTable<T>(
        {
            ...props,
            columns,
            manualPagination: true,
            manualSorting: true,
            pageCount,
            getRowId: (row: any) => row.id.toString(),
            onRowSelectionChange: (newState) => {
                if(setRowSelection) {
                    setRowSelection(newState)
                }
            },
            state: {
                ...props.initialState,
                pagination,
                sorting,
                rowSelection: props.rowSelection||{}
            },
            getCoreRowModel: getCoreRowModel(),
            getSortedRowModel: getSortedRowModel(),
        }
    );

    const getSortIcon = (sortId: string) => sortId !== sortKey ? 'fluent:arrow-sort-16-filled' : sortOrder === 'desc' ? 'fluent:arrow-sort-down-16-filled' : 'fluent:arrow-sort-up-16-filled'

    const handlePageSizeChange = (pageSize: number) => {
        let newMeta: SetMetaInput<T> = {};
        if (pageSize !== limit) {
            newMeta.limit = pageSize;
            newMeta.offset = 0;
            newMeta.page = 1;
        }
        if (Object.keys(newMeta).length) {
            onMetaChange(newMeta);
        }
    }

    const handlePageChange = (nextPage: number) => {
        let newMeta: SetMetaInput<T> = {};
        if (nextPage !== currentPage) {
            newMeta.page = nextPage;
            newMeta.offset = nextPage * limit - limit;
        }
        if (Object.keys(newMeta).length) {
            onMetaChange(newMeta);
        }
    }

    const handleSort = (key: keyof T, relationId: string | undefined, headerId: string) => {
        let newMeta: SetMetaInput<T> = {};
        if (headerId !== sortKey) {
            newMeta.sortKey = headerId;
            newMeta.sortOrder = sortOrder
        } else {
            if (sortOrder === 'asc') {
                newMeta.sortOrder = 'desc';
            } else {
                newMeta.sortOrder = 'asc';
            }
        }
        if (relationId) {
            newMeta.orderBy = {
                [relationId]: {
                    [key]: newMeta.sortOrder
                }
            }
        } else {
            newMeta.orderBy = {
                [key]: newMeta.sortOrder
            }
        }
        if (Object.keys(newMeta).length) {
            onMetaChange(newMeta);
        }
    }

    return (
        <Box>
            <S.DataTableComponent>
                <S.TableHead>
                    {table.getHeaderGroups().map((headerGroup) => {
                        return (
                            <S.TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    let colDef = header.column.columnDef
                                    //@ts-ignore
                                    let metaSortKey = colDef.meta?.sortKey
                                    //@ts-ignore
                                    let metaRelationKey = colDef.meta?.relationKey
                                    let sortId = metaSortKey || colDef.id || header.id;
                                    let sortingEnabled = colDef.enableSorting === false ? false : true
                                    const sortIconName = getSortIcon(header.id);
                                    return (
                                        <S.TableHeading key={header.id} colSpan={header.colSpan}>    
                                                <S.SortIconButton
                                                    onClick={() => {
                                                        if(sortingEnabled) {
                                                            handleSort(sortId, metaRelationKey, header.id)
                                                        }
                                                    }}
                                                    title="Toggle SortBy"
                                                    className={clsx({ sorted: header.id === sortKey })}
                                                >
                                                    <span>
                                                        {flexRender(
                                                            header.column.columnDef.header,
                                                            header.getContext()
                                                        )}
                                                    </span>
                                                    {sortingEnabled&&header.id!=='select'&&<S.TableHeaderIcon icon={sortIconName} />}
                                                </S.SortIconButton>
                                            
                                        </S.TableHeading>
                                    )
                                })}
                            </S.TableRow>
                        )
                    })}
                </S.TableHead>
                <S.TableBody>
                    {table.getRowModel().rows.map((row) => {
                        let hasHighlight = false
                        if(props.highlights&&(Object.keys(props.highlights).length>0)) {
                            Object.keys(props.highlights).forEach((key) => {
                                let value = row.getValue(key) as number
                                if (value>0) {
                                    hasHighlight = true
                                }
                            })                            
                        }
                        return (
                            <S.TableRow 
                                key={row.id} 
                                className={clsx({ selected: row.getValue('id') === selectedId, alert: hasHighlight })}
                            >
                                {row.getVisibleCells().map(cell => {
                                    return (
                                        <S.TableCell
                                            key={cell.id}
                                            onClick={()=>{
                                                if(cell.column.id!=='select') {
                                                    onSelectItem(row.getValue('id'))
                                                } else {
                                                    return row.toggleSelected()
                                                }
                                            }}
                                        >
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext()
                                            )}
                                        </S.TableCell>
                                    )
                                })}
                            </S.TableRow>
                        )
                    })}
                </S.TableBody>
            </S.DataTableComponent>
            <S.PaginationContainer>

                <Group gap={6}>
                    <span>Page:</span>
                    <Select
                        size="xs"
                        placeholder="Select..."
                        data={table.getPageOptions().map(i => (i + 1).toString())}
                        onChange={(value) => handlePageChange(Number(value))}
                        value={currentPage.toString()}
                        style={{ width: '70px' }}
                    />
                    <span>of {table.getPageCount()}</span>
                </Group>

                <Group gap={2}>
                    <Button
                        size="xs"
                        variant="light"
                        color="gray"
                        onClick={() => handlePageChange(1)}
                        disabled={!canPreviousPage}
                        style={{
                            padding: '0 8px'
                        }}
                    >
                        <Icon icon='ci:chevron-left-duo' style={{ fontSize: '16px' }} />
                    </Button>
                    <Button
                        size="xs"
                        variant="light"
                        color="gray"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={!canPreviousPage}
                        style={{
                            padding: '0 8px'
                        }}
                    >
                        <Icon icon='ci:chevron-left-md' style={{ fontSize: '16px' }} />
                    </Button>

                    <Button
                        size="xs"
                        variant="light"
                        color="gray"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={!canNextPage}
                        style={{
                            padding: '0 8px'
                        }}
                    >
                        <Icon icon='ci:chevron-right-md' style={{ fontSize: '16px' }} />
                    </Button>
                    <Button
                        size="xs"
                        variant="light"
                        color="gray"
                        onClick={() => handlePageChange(pageCount)}
                        disabled={!canNextPage}
                        style={{
                            padding: '0 8px'
                        }}
                    >
                        <Icon icon='ci:chevron-right-duo' style={{ fontSize: '16px' }} />
                    </Button>
                </Group>

                <Group gap={6}>
                    <span>Items Per Page: </span>
                    <Select
                        size="xs"
                        placeholder="Select..."
                        data={['1','5', '10', '20', '50', '100']}
                        onChange={(value) => handlePageSizeChange(Number(value))}
                        value={limit.toString()}
                        style={{ width: '70px' }}
                    />
                </Group>

            </S.PaginationContainer>
        </Box>
    )

}